import type { CSSProperties } from 'react';
import type { WidgetInstanceProps } from '@source-components-react/types';
import {
  resolveMountName,
  resolveMountProps,
  type MountsMap,
} from './mount-attributes';

const PLACEHOLDER_STYLE: CSSProperties = {
  boxSizing: 'border-box',
  border: '1px dashed #94a3b8',
  borderRadius: 4,
  padding: '12px 16px',
  color: '#64748b',
  fontSize: 13,
  lineHeight: 1.4,
  fontFamily: 'system-ui, sans-serif',
  backgroundColor: '#f8fafc',
};

const warnUnmatchedMount = (name: string, registered: string[]): void => {
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'production') {
    return;
  }
  const available = registered.length ? registered.join(', ') : '(none)';
  console.warn(
    `r-mount: no host component registered as "${name}". Registered names: ${available}.`
  );
};

/**
 * A leaf that renders a host-app component by name.
 *
 * The author sets a name and a list of typed props in the composer. The host
 * application passes the actual component on `RuleCMSWidget`'s `mounts` map.
 * This file never imports customer code: a miss is a placeholder, not a
 * lookup into executable text.
 *
 * The root is always a `<div>`. The host component is unknown, so it cannot
 * be trusted to carry column styles or a className itself. Author props are
 * never spread onto this div — only onto the resolved host component.
 */
export function RMount({
  componentAttributes,
  styleProps,
  className,
  mounts,
  widgetInstanceProps,
}: Readonly<{
  componentAttributes?: { [key: string]: any };
  styleProps?: { [key: string]: any };
  className?: string;
  mounts?: MountsMap;
  widgetInstanceProps?: WidgetInstanceProps;
}>) {
  const name = resolveMountName(componentAttributes);
  const HostComponent = name && mounts ? mounts[name] : undefined;

  if (name && mounts && !HostComponent) {
    warnUnmatchedMount(name, Object.keys(mounts));
  }

  if (HostComponent) {
    const authorProps = resolveMountProps(componentAttributes);
    return (
      <div style={styleProps} {...(className !== undefined ? { className } : {})}>
        <HostComponent {...widgetInstanceProps} {...authorProps} />
      </div>
    );
  }

  const style: CSSProperties = {
    ...PLACEHOLDER_STYLE,
    ...styleProps,
  };

  return (
    <div style={style} {...(className !== undefined ? { className } : {})}>
      {name ? name : 'Custom Component'}
    </div>
  );
}
